"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

const LEAVE_MS = 150;

export function useFloatingTooltip(
  triggerRef: RefObject<HTMLElement | null>,
) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearTimers = useCallback(() => {
    clearTimeout(unmountTimerRef.current);
  }, []);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    setPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [triggerRef]);

  const open = useCallback(() => {
    clearTimers();

    if (mounted) {
      updatePosition();
      setVisible(true);
      return;
    }

    updatePosition();
    setMounted(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
  }, [clearTimers, mounted, updatePosition]);

  const close = useCallback(() => {
    clearTimers();

    if (!mounted) {
      return;
    }

    setVisible(false);
    unmountTimerRef.current = setTimeout(() => {
      setMounted(false);
    }, LEAVE_MS);
  }, [clearTimers, mounted]);

  useEffect(() => {
    if (!mounted || !visible) {
      return;
    }

    const reposition = () => updatePosition();

    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [mounted, visible, updatePosition]);

  useEffect(() => clearTimers, [clearTimers]);

  return {
    mounted,
    visible,
    position,
    open,
    close,
    leaveMs: LEAVE_MS,
  };
}
